import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AppointmentCampaignSentLogService from "../../services/appointmentcampaignSentLog.service";
import { computeCampaignAnalyticsFromLogs } from "../../pages/marketing/CampaignDailyLogs/utils/computeCampaignAnalyticsFromLogs";
import HandleResponse from "../HandleResponse";
import HandleError from "../HandleError";

const parseAnalyticsPayload = (data) => (data?.summary ? data : data?.data ?? data);

export const CampaignLogFilterOptionsApi = createAsyncThunk(
  "campaignsentlog/filteroptions",
  async (_, thunkAPI) => {
    try {
      const response = await AppointmentCampaignSentLogService.fetchFilterOptions();
      return HandleResponse(thunkAPI, response, "filteroptions");
    } catch (error) {
      return HandleError(thunkAPI, error, "filteroptions");
    }
  }
);

export const CampaignLogAnalyticsApi = createAsyncThunk(
  "campaignsentlog/analytics",
  async (formValues, thunkAPI) => {
    const period = formValues?.period ?? formValues?.days ?? "30";

    const loadFromLogs = async () => {
      const logsRes = await AppointmentCampaignSentLogService.fetchLogs({
        ...formValues,
        period,
        page: 1,
        limit: 500,
      });
      if (logsRes.status === 200 && logsRes.data?.data?.length) {
        return computeCampaignAnalyticsFromLogs(logsRes.data.data, period);
      }
      return null;
    };

    try {
      const response = await AppointmentCampaignSentLogService.fetchAnalytics(formValues);
      if (response.status === 200) {
        const payload = parseAnalyticsPayload(response.data);
        // A successful observability response is authoritative, including an
        // empty result.  Fetching 500 log rows to synthesize a zero chart
        // keeps the filters disabled and makes the page wait on a second,
        // unbounded request.  Keep the legacy fallback only for transport or
        // route failures below.
        return payload;
      }
    } catch (error) {
      const fallback = await loadFromLogs();
      if (fallback) {
        return fallback;
      }
      return HandleError(thunkAPI, error, "analytics");
    }

    const fallback = await loadFromLogs();
    if (fallback) {
      return fallback;
    }
    return thunkAPI.rejectWithValue();
  }
);

export const CampaignLogsListApi = createAsyncThunk(
  "campaignsentlog/logslist",
  async (formValues, thunkAPI) => {
    try {
      const response = await AppointmentCampaignSentLogService.fetchLogs(formValues);
      return HandleResponse(thunkAPI, response, "logslist");
    } catch (error) {
      return HandleError(thunkAPI, error, "logslist");
    }
  }
);

export const CampaignLogDailySummaryApi = createAsyncThunk(
  "campaignsentlog/dailysummary",
  async (formValues, thunkAPI) => {
    try {
      const response = await AppointmentCampaignSentLogService.fetchDailySummary(formValues);
      if (response.status === 200) {
        return response.data?.data !== undefined ? response.data : response.data?.data ?? response.data;
      }
      return thunkAPI.rejectWithValue(response.data);
    } catch (error) {
      return HandleError(thunkAPI, error, "dailysummary");
    }
  }
);

export const CampaignLogResendApi = createAsyncThunk(
  "campaignsentlog/resend",
  async (logId, thunkAPI) => {
    try {
      const response = await AppointmentCampaignSentLogService.resendLog(logId);
      if (response.status === 200) {
        return response.data;
      }
      return thunkAPI.rejectWithValue(response.data);
    } catch (error) {
      return HandleError(thunkAPI, error, "resend");
    }
  }
);

const initialState = {
  filterOptions: { campaigns: [], types: [] },
  analytics: null,
  analyticsPeriod: "30",
  customDateFrom: "",
  customDateTo: "",
  dailySummary: null,
  dailyPage: 1,
  dailyPerPage: 15,
  logsList: null,
  logsPage: 1,
  logsPerPage: 20,
  statusFilter: "",
  typeFilter: "",
  campaignFilter: "",
  scheduleFilter: "",
  clientNameFilter: "",
  clientEmailFilter: "",
  clientPhoneFilter: "",
  selectedDate: "",
  resendingLogId: null,
  isApiStatus: {
    CampaignLogFilterOptionsApi: "",
    CampaignLogAnalyticsApi: "",
    CampaignLogDailySummaryApi: "",
    CampaignLogsListApi: "",
    CampaignLogResendApi: "",
  },
};

const campaignSentLogSlice = createSlice({
  name: "campaignsentlog",
  initialState,
  reducers: {
    SetCampaignLogStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
    },
    SetCampaignLogTypeFilter: (state, action) => {
      state.typeFilter = action.payload;
    },
    SetCampaignLogCampaignFilter: (state, action) => {
      state.campaignFilter = action.payload;
    },
    SetCampaignLogScheduleFilter: (state, action) => {
      state.scheduleFilter = action.payload;
    },
    SetCampaignLogSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    SetCampaignLogsPage: (state, action) => {
      state.logsPage = action.payload;
    },
    SetCampaignLogsPerPage: (state, action) => {
      state.logsPerPage = action.payload;
    },
    SetCampaignLogAnalyticsPeriod: (state, action) => {
      state.analyticsPeriod = action.payload;
    },
    SetCampaignLogCustomDateFrom: (state, action) => {
      state.customDateFrom = action.payload;
    },
    SetCampaignLogCustomDateTo: (state, action) => {
      state.customDateTo = action.payload;
    },
    SetCampaignLogClientNameFilter: (state, action) => {
      state.clientNameFilter = action.payload;
    },
    SetCampaignLogClientEmailFilter: (state, action) => {
      state.clientEmailFilter = action.payload;
    },
    SetCampaignLogClientPhoneFilter: (state, action) => {
      state.clientPhoneFilter = action.payload;
    },
    SetCampaignDailyPage: (state, action) => {
      state.dailyPage = action.payload;
    },
    SetCampaignDailyPerPage: (state, action) => {
      state.dailyPerPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(CampaignLogFilterOptionsApi.pending, (state) => {
        state.isApiStatus.CampaignLogFilterOptionsApi = "loading";
      })
      .addCase(CampaignLogFilterOptionsApi.fulfilled, (state, action) => {
        state.isApiStatus.CampaignLogFilterOptionsApi = "succeeded";
        state.filterOptions = action.payload ?? state.filterOptions;
      })
      .addCase(CampaignLogFilterOptionsApi.rejected, (state) => {
        state.isApiStatus.CampaignLogFilterOptionsApi = "failed";
      })
      .addCase(CampaignLogAnalyticsApi.pending, (state) => {
        state.isApiStatus.CampaignLogAnalyticsApi = "loading";
      })
      .addCase(CampaignLogAnalyticsApi.fulfilled, (state, action) => {
        state.isApiStatus.CampaignLogAnalyticsApi = "succeeded";
        state.analytics = action.payload;
      })
      .addCase(CampaignLogAnalyticsApi.rejected, (state) => {
        state.isApiStatus.CampaignLogAnalyticsApi = "failed";
      })
      .addCase(CampaignLogsListApi.pending, (state) => {
        state.isApiStatus.CampaignLogsListApi = "loading";
      })
      .addCase(CampaignLogsListApi.fulfilled, (state, action) => {
        state.isApiStatus.CampaignLogsListApi = "succeeded";
        state.logsList = action.payload;
      })
      .addCase(CampaignLogsListApi.rejected, (state) => {
        state.isApiStatus.CampaignLogsListApi = "failed";
      })
      .addCase(CampaignLogDailySummaryApi.pending, (state) => {
        state.isApiStatus.CampaignLogDailySummaryApi = "loading";
      })
      .addCase(CampaignLogDailySummaryApi.fulfilled, (state, action) => {
        state.isApiStatus.CampaignLogDailySummaryApi = "succeeded";
        state.dailySummary = action.payload;
      })
      .addCase(CampaignLogDailySummaryApi.rejected, (state) => {
        state.isApiStatus.CampaignLogDailySummaryApi = "failed";
      })
      .addCase(CampaignLogResendApi.pending, (state, action) => {
        state.isApiStatus.CampaignLogResendApi = "loading";
        state.resendingLogId = action.meta.arg;
      })
      .addCase(CampaignLogResendApi.fulfilled, (state) => {
        state.isApiStatus.CampaignLogResendApi = "succeeded";
        state.resendingLogId = null;
      })
      .addCase(CampaignLogResendApi.rejected, (state) => {
        state.isApiStatus.CampaignLogResendApi = "failed";
        state.resendingLogId = null;
      });
  },
});

export const {
  SetCampaignLogStatusFilter,
  SetCampaignLogTypeFilter,
  SetCampaignLogCampaignFilter,
  SetCampaignLogScheduleFilter,
  SetCampaignLogSelectedDate,
  SetCampaignLogsPage,
  SetCampaignLogsPerPage,
  SetCampaignLogAnalyticsPeriod,
  SetCampaignLogCustomDateFrom,
  SetCampaignLogCustomDateTo,
  SetCampaignLogClientNameFilter,
  SetCampaignLogClientEmailFilter,
  SetCampaignLogClientPhoneFilter,
  SetCampaignDailyPage,
  SetCampaignDailyPerPage,
} = campaignSentLogSlice.actions;

export default campaignSentLogSlice.reducer;
